/**
 * localStorage utilities for authentication
 * Replaces cookie-based auth with localStorage
 */

const STORAGE_KEYS = {
	ACCESS_TOKEN: 'access_token',
	USER: 'user',
	PLAN: 'plan'
} as const;

export type StorageKeys = typeof STORAGE_KEYS[keyof typeof STORAGE_KEYS];
export const AUTH_USER_UPDATED_EVENT = 'auth:user-updated';
export const AUTH_PLAN_UPDATED_EVENT = 'auth:plan-updated';

/**
 * Get item from localStorage
 */
export function getStorageItem(key: StorageKeys): string | null {
	if (typeof window === 'undefined') {
		return null;
	}
	try {
		return localStorage.getItem(key);
	} catch (error) {
		console.error(`Error getting ${key} from localStorage:`, error);
		return null;
	}
}

/**
 * Set item in localStorage
 */
export function setStorageItem(key: StorageKeys, value: string): void {
	if (typeof window === 'undefined') {
		return;
	}
	try {
		localStorage.setItem(key, value);
	} catch (error) {
		console.error(`Error setting ${key} in localStorage:`, error);
	}
}

/**
 * Remove item from localStorage
 */
export function removeStorageItem(key: StorageKeys): void {
	if (typeof window === 'undefined') {
		return;
	}
	try {
		localStorage.removeItem(key);
	} catch (error) {
		console.error(`Error removing ${key} from localStorage:`, error);
	}
}

/**
 * Clear all auth-related items from localStorage
 */
export function clearAuthStorage(): void {
	Object.values(STORAGE_KEYS).forEach((key) => {
		removeStorageItem(key);
	});
}

/**
 * Get access token from localStorage
 */
export function getAccessToken(): string | null {
	return getStorageItem(STORAGE_KEYS.ACCESS_TOKEN);
}

/**
 * Set access token in localStorage
 */
export function setAccessToken(token: string): void {
	setStorageItem(STORAGE_KEYS.ACCESS_TOKEN, token);
}

/**
 * Get user data from localStorage
 */
export function getUser(): any | null {
	const userStr = getStorageItem(STORAGE_KEYS.USER);
	if (!userStr) {
		return null;
	}
	try {
		return JSON.parse(userStr);
	} catch (error) {
		console.error('Error parsing user data from localStorage:', error);
		return null;
	}
}

/**
 * Set user data in localStorage
 */
export function setUser(user: any): void {
	setStorageItem(STORAGE_KEYS.USER, JSON.stringify(user));

	if (typeof window !== 'undefined') {
		window.dispatchEvent(
			new CustomEvent(AUTH_USER_UPDATED_EVENT, {
				detail: user
			})
		);
	}
}

/**
 * Get plan data from localStorage
 */
export function getPlan(): any | null {
	const planStr = getStorageItem(STORAGE_KEYS.PLAN);
	if (!planStr) {
		return null;
	}
	try {
		return JSON.parse(planStr);
	} catch (error) {
		console.error('Error parsing plan data from localStorage:', error);
		return null;
	}
}

/**
 * Set plan data in localStorage
 */
export function setPlan(plan: any): void {
	setStorageItem(STORAGE_KEYS.PLAN, JSON.stringify(plan));

	if (typeof window !== 'undefined') {
		window.dispatchEvent(
			new CustomEvent(AUTH_PLAN_UPDATED_EVENT, {
				detail: plan
			})
		);
	}
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
	const token = getAccessToken();
	const user = getUser();
	return !!(token && user);
}

export { STORAGE_KEYS };

